import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { TableColumnConfig } from '../tableMain';
import './tableCell.css';

interface EditableCellProps {
    rowData: any;
    value: string | number;
    displayValue: string;
    inputRef?: React.RefObject<HTMLInputElement | HTMLSelectElement>;
    cellConfig: TableColumnConfig;
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
    onBlur: () => void;
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement | HTMLSelectElement>) => void;
    isDisabled?: boolean;
}

const EditableCell: React.FC<EditableCellProps> = ({
    value,
    // displayValue,
    rowData,
    inputRef,
    onChange,
    onBlur,
    onKeyDown,
    isDisabled,
    cellConfig,
}) => {

    useEffect(() => {
        if (inputRef?.current) {
            inputRef.current.focus();
        }
    }, [inputRef]);

    const displayValue = value ?? '';

    const inputContainerRef = useRef<HTMLInputElement>(null);
    const typeaheadListRef = useRef<HTMLUListElement>(null);
    const [typeaheadOpen, setTypeaheadOpen] = useState(false);
    const [typeaheadPos, setTypeaheadPos] = useState<{ top: number; left: number; width: number } | null>(null);

    const openTypeahead = () => {
        if (inputContainerRef.current) {
            const rect = inputContainerRef.current.getBoundingClientRect();
            setTypeaheadPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX, width: rect.width });
        }
        setTypeaheadOpen(true);
    };

    useEffect(() => {
        if (!typeaheadOpen) return;
        const close = (e: Event) => {
            // Don't close if the scroll originated within the typeahead dropdown itself
            if (typeaheadListRef.current && typeaheadListRef.current.contains(e.target as Node)) return;
            setTypeaheadOpen(false);
        };
        // capture=true catches scroll on any ancestor (including the table container)
        window.addEventListener('scroll', close, true);
        return () => window.removeEventListener('scroll', close, true);
    }, [typeaheadOpen]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;

        if (cellConfig.inputType !== 'dropdown' && (cellConfig.colType === 'number' || cellConfig.colType === 'coordinate')) {
            // Allow empty string (for clearing), or valid numbers
            if (inputValue === '' || /^-?\d*\.?\d*$/.test(inputValue)) {
                onChange(e); // Pass the event up if valid
            }
            // Ignore invalid input (do not update state)
            return;
        }

        if (cellConfig.inputType !== 'dropdown' && cellConfig.colType === 'boolean') {
            if (inputValue === '' || inputValue === '0' || inputValue === '1') {
                onChange(e);
            }
            return;
        }

        onChange(e);
    };

    if (cellConfig.inputType === 'typeahead' && cellConfig.dropdownOptions) {
        const allOptions: { value: string; label: string }[] = cellConfig.dropdownOptions.map((o: any) =>
            typeof o === 'object' ? { value: String(o.value), label: String(o.label) } : { value: String(o), label: String(o) }
        );
        const filtered = String(displayValue).trim()
            ? allOptions.filter(o => o.label.toLowerCase().includes(String(displayValue).toLowerCase()))
            : allOptions;

        return (
            <>
                <input
                    ref={(el) => {
                        if (inputRef) {
                            (inputRef as React.MutableRefObject<HTMLInputElement | HTMLSelectElement | null>).current = el;
                        }
                        (inputContainerRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
                    }}
                    disabled={isDisabled}
                    value={displayValue}
                    onChange={handleInputChange}
                    onFocus={openTypeahead}
                    onBlur={(e) => {
                        // Delay so option mousedown fires first
                        setTimeout(() => setTypeaheadOpen(false), 150);
                        onBlur();
                    }}
                    onKeyDown={onKeyDown}
                    className='tableCell-dropdown'
                    style={{ width: cellConfig.enableSidepanel ? 'calc(100% - 30px)' : '100%' }}
                    autoComplete='off'
                />
                {typeaheadOpen && typeaheadPos && filtered.length > 0 && createPortal(
                    <ul ref={typeaheadListRef} className='tableCell-typeahead-list' style={{
                        top: typeaheadPos.top,
                        left: typeaheadPos.left,
                        width: typeaheadPos.width,
                    }}>
                        {filtered.map(o => (
                            <li
                                key={o.value}
                                className='tableCell-typeahead-option'
                                onMouseDown={() => {
                                    const syntheticEvent = { target: { value: o.value } } as React.ChangeEvent<HTMLInputElement>;
                                    onChange(syntheticEvent);
                                    setTypeaheadOpen(false);
                                }}
                            >
                                {o.label}
                            </li>
                        ))}
                    </ul>,
                    document.body
                )}
            </>
        );
    }

    if (cellConfig.inputType === 'dropdown' && cellConfig.dropdownOptions) {
        let dropdownOptions = cellConfig.dropdownOptions || [];
        let isDropdownDisabled = isDisabled;

        // Handle dependent dropdowns
        if (cellConfig.dependsOn && cellConfig.getFilteredOptions) {
            const dependentValue = rowData[cellConfig.dependsOn];
            if (!dependentValue) {  
                // Empty dropdown if dependent value is not selected
                dropdownOptions = [];
            } else {
                // Filter options based on dependent value
                dropdownOptions = cellConfig.getFilteredOptions(dependentValue, cellConfig.dropdownOptions || [], rowData);
            }
        }

        const currentOption = dropdownOptions.find((opt: any) => 
            (typeof opt === 'object' ? opt.value : opt) === displayValue
        );
        const displayLabel = currentOption 
            ? (typeof currentOption === 'object' ? currentOption.label : currentOption)
            : displayValue;
            
        return (
            <select
                ref={inputRef as React.RefObject<HTMLSelectElement>}
                disabled={isDropdownDisabled}
                value={displayValue}
                onBlur={onBlur}
                onChange={e => {
                    onChange(e);
                }}
                onKeyDown={onKeyDown as any}
                className='tableCell-dropdown'
                style={{
                    width: cellConfig.enableSidepanel ? 'calc(100% - 30px)' : '100%',
                }}
            >
                {/* Placeholder value */}
                <option disabled value={displayValue}>
                    {displayLabel}
                </option>
                {/* Empty/null option */}
                <option value="">(Empty)</option>
                {dropdownOptions.map((option: any) => {
                    if (typeof option === 'object' && option !== null) {
                        return (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        );
                    }
                    return (
                        <option key={option} value={option}>
                            {option}
                        </option>
                    );
                })}
            </select>
        );
    }

    return (
        <input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            disabled={isDisabled}
            value={displayValue}
            onChange={handleInputChange}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            className='tableCell-cellValue tableCell-cellValue--editable'
            style={{
                height: '100%',
                width: cellConfig.enableSidepanel ? 'calc(100% - 30px)' : '100%',
            }}
        />
    );
};

export default EditableCell;